import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * The deferred-media methods the video autoplay relies on (media.js does not export the class).
 *
 * @typedef {HTMLElement & {
 *   isPlaying: boolean,
 *   loadContent: (focus?: boolean) => void,
 *   playMedia: () => void,
 *   pauseMedia: () => void,
 *   updatePlayPauseHint: (isPlaying: boolean) => void,
 * }} GalleryVideo
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-video-autoplay') && !prefersReducedMotion()) {
      customElements.whenDefined('deferred-media').then(() => {
        if (this.isConnected) this.#observeVideos();
      });
    }
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #videoObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoObserver?.disconnect();
  }

  /**
   * Plays each gallery video while at least half of it is on screen and pauses it once it leaves, so a
   * carousel only plays the visible slide. The zoom dialog's copies are left alone. The videos are muted
   * (snippets/video.liquid), which browsers require for autoplay.
   */
  #observeVideos() {
    const zoomDialog = this.refs.zoomDialogComponent;
    const videos = [...this.querySelectorAll('deferred-media')].filter((element) => !zoomDialog?.contains(element));
    if (!videos.length) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {GalleryVideo} */ (entry.target);

          if (entry.isIntersecting) this.#playVideo(video);
          else if (video.isPlaying) video.pauseMedia();
        }
      },
      { threshold: 0.5 }
    );

    for (const video of videos) this.#videoObserver.observe(video);
  }

  /**
   * Starts a gallery video, loading it first if the visitor has not played it yet.
   *
   * @param {GalleryVideo} video - The deferred-media element holding the video.
   */
  #playVideo(video) {
    if (video.hasAttribute('data-media-loaded')) {
      video.playMedia();
      return;
    }

    // Same as clicking the poster, minus the focus move, which would scroll the page to the video.
    video.loadContent(false);
    video.isPlaying = true;
    video.updatePlayPauseHint(true);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
